import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useContent } from '../hooks/useContent';
import { MenuIcon } from './icons/MenuIcon';
import { XIcon } from './icons/XIcon';

export const Header: React.FC = () => {
  const { user, logout, setModalView } = useAuth();
  const { isAdminMode, toggleAdminMode, headerNav } = useContent();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const navLinks = headerNav?.links || [];

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const handleLogin = () => {
    setModalView('login');
    setIsMenuOpen(false);
  };

  const handleSignup = () => {
    setModalView('signup');
    setIsMenuOpen(false);
  };

  const isAdmin = user && user.role === 'admin';

  return (
    <header className="bg-white shadow-md sticky top-0 z-40">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="block">
              <img
                src="/brand-name-generator-logo.png"
                alt="BrandGenius App: Brand Name Generator Logo"
                className="h-10 w-auto"
                width="146"
                height="40"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map(link =>
              link.children && link.children.length > 0 ? (
                <div key={link.id} className="relative group">
                  <Link
                    to={link.path || '/industries/'}
                    className={`inline-flex items-center gap-1 font-medium transition-colors py-2 ${
                      currentPath.startsWith(link.path || '______')
                        ? 'text-[#4F46E5]'
                        : 'text-[#1F2937] hover:text-[#4F46E5]'
                    }`}
                  >
                    <span>{link.name}</span>
                    <svg className="w-4 h-4 text-gray-400 group-hover:text-[#4F46E5] transition-transform duration-200 group-hover:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Link>
                  <div
                    className={`absolute left-0 mt-0 bg-white shadow-xl rounded-xl border border-gray-100 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-200 z-50 ${
                      link.children.length > 8
                        ? 'w-[620px] lg:w-[760px] p-4 max-h-[80vh] overflow-y-auto'
                        : 'w-56 p-2'
                    }`}
                  >
                    {link.children.length > 8 && (
                      <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100 px-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                          Specialized Industry Generators ({link.children.length})
                        </span>
                        <Link
                          to="/industries/"
                          className="text-xs font-bold text-[#4F46E5] hover:underline"
                        >
                          View All Industries Directory →
                        </Link>
                      </div>
                    )}
                    <div
                      className={
                        link.children.length > 8
                          ? 'grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-1'
                          : 'space-y-1'
                      }
                    >
                      {link.children.map(child => (
                        <Link
                          key={child.id}
                          to={child.path}
                          className={`block px-3 py-2 text-sm rounded-lg transition-colors truncate ${
                            currentPath === child.path
                              ? 'bg-indigo-50 text-[#4F46E5] font-semibold'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-[#4F46E5]'
                          }`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.id}
                  to={link.path}
                  className={`font-medium transition-colors ${
                    currentPath === link.path
                      ? 'text-[#4F46E5]'
                      : 'text-[#1F2937] hover:text-[#4F46E5]'
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}

            {user && (
              <Link
                to="/dashboard/"
                className={`font-medium transition-colors ${
                  currentPath === '/dashboard/'
                    ? 'text-[#4F46E5]'
                    : 'text-[#1F2937] hover:text-[#4F46E5]'
                }`}
              >
                Dashboard
              </Link>
            )}
          </nav>

          {/* Auth buttons & Hamburger */}
          <div className="flex items-center">
            <div className="hidden md:flex items-center space-x-4">
              {isAdmin && (
                <button
                  onClick={toggleAdminMode}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full border-2 ${
                    isAdminMode
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-transparent text-indigo-600 border-indigo-500'
                  }`}
                >
                  Admin
                </button>
              )}
              {user ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Welcome, {user.name}</span>
                    {user.isPro && (
                      <span className="px-2 py-0.5 text-xs font-semibold text-indigo-700 bg-indigo-100 rounded-full">
                        Pro
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="font-medium text-gray-600 hover:text-[#4F46E5] transition-colors"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleLogin}
                    className="font-medium text-[#4F46E5] hover:text-[#6366F1] transition-colors"
                  >
                    Login
                  </button>
                  <button
                    onClick={handleSignup}
                    className="px-4 py-2 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-semibold rounded-lg shadow-sm transition-colors duration-300 text-sm"
                  >
                    Sign Up
                  </button>
                </>
              )}
            </div>

            {/* Hamburger */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-[#4F46E5] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#4F46E5]"
                aria-label={isMenuOpen ? 'Close main menu' : 'Open main menu'}
              >
                {isMenuOpen ? <XIcon className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu: links always in DOM */}
      <nav
        className={`md:hidden absolute top-16 inset-x-0 bg-white shadow-lg z-50 p-4 transition-all duration-300 ${
          isMenuOpen ? 'block' : 'hidden'
        }`}
      >
        {navLinks.map(link =>
          link.children && link.children.length > 0 ? (
            <div key={link.id} className="mb-2">
              <details className="group">
                <summary className="cursor-pointer px-4 py-2 rounded-md font-medium text-[#1F2937] hover:bg-gray-100">
                  <Link to={link.path || '#'} onClick={() => setIsMenuOpen(false)}>{link.name}</Link>
                </summary>
                <div className="ml-4 mt-1">
                  {link.children.map(child => (
                    <Link
                      key={child.id}
                      to={child.path}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {child.name}
                    </Link>
                  ))}
                </div>
              </details>
            </div>
          ) : (
            <Link
              key={link.id}
              to={link.path}
              className="block px-4 py-2 rounded-md font-medium text-[#1F2937] hover:bg-gray-100"
              onClick={() => setIsMenuOpen(false)}
            >
              {link.name}
            </Link>
          )
        )}
        {user && (
          <Link
            to="/dashboard/"
            className="block px-4 py-2 rounded-md font-medium text-[#1F2937] hover:bg-gray-100"
          >
            Dashboard
          </Link>
        )}

        {/* Admin & Auth buttons mobile */}
        <div className="pt-4 mt-4 border-t border-gray-200 space-y-2">
          {isAdmin && (
            <button
              onClick={toggleAdminMode}
              className={`w-full text-center px-4 py-2 text-sm font-semibold rounded-md border-2 ${
                isAdminMode
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-transparent text-indigo-600 border-indigo-500'
              }`}
            >
              Admin
            </button>
          )}
          {user ? (
            <button
              onClick={handleLogout}
              className="w-full text-left block px-4 py-2 rounded-md font-medium text-[#1F2937] hover:bg-gray-100"
            >
              Logout
            </button>
          ) : (
            <>
              <button
                onClick={handleLogin}
                className="w-full text-left block px-4 py-2 rounded-md font-medium text-[#1F2937] hover:bg-gray-100"
              >
                Login
              </button>
              <button
                onClick={handleSignup}
                className="w-full text-left block px-4 py-2 rounded-md font-medium bg-[#4F46E5] text-white hover:bg-[#6366F1]"
              >
                Sign Up
              </button>
            </>
          )}
        </div>
      </nav>
    </header>
  );
};
